/**
 * Logger Utility
 * Centralized logging with environment-aware output
 * Production logs are suppressed, development logs are displayed
 * warn/error calls are persisted to Firestore eventLogs collection
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { COLLECTIONS } from '../constants/collections';

const isDevelopment = process.env.NODE_ENV === 'development';

/** Simple dedup: track recent messages to skip duplicates within 5s */
const _recentMessages = new Map();
const DEDUP_INTERVAL_MS = 5000;

/**
 * Persist a warn/error log entry to Firestore (fire-and-forget).
 * Entire function is wrapped in try/catch so logging never crashes the app.
 */
function _persistLog(level, args) {
  try {
    const message = args
      .map((a) => (typeof a === 'string' ? a : JSON.stringify(a, null, 2)))
      .join(' ');

    // Dedup: skip if same level+message logged within last 5 seconds
    const dedupKey = `${level}:${message}`;
    const now = Date.now();
    const lastSeen = _recentMessages.get(dedupKey);
    if (lastSeen && now - lastSeen < DEDUP_INTERVAL_MS) {
      return;
    }
    _recentMessages.set(dedupKey, now);

    // Prune old entries periodically (keep map from growing forever)
    if (_recentMessages.size > 200) {
      for (const [key, ts] of _recentMessages) {
        if (now - ts > DEDUP_INTERVAL_MS) _recentMessages.delete(key);
      }
    }

    const currentUser = auth.currentUser;
    const details = args.length > 1 ? args.slice(1) : undefined;
    let stackTrace = null;
    if (level === 'error') {
      const errArg = args.find((a) => a instanceof Error);
      stackTrace = errArg ? errArg.stack : new Error().stack;
    }

    const entry = {
      level,
      message: typeof args[0] === 'string' ? args[0] : JSON.stringify(args[0]),
      details: details ? JSON.stringify(details) : null,
      source: typeof args[0] === 'string' ? args[0].split(':')[0] : null,
      stackTrace,
      userId: currentUser?.uid || null,
      userEmail: currentUser?.email || null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      environment: process.env.NODE_ENV || 'unknown',
      createdAt: serverTimestamp(),
    };

    addDoc(collection(db, COLLECTIONS.EVENT_LOGS), entry).catch(() => {});
  } catch {
    // Never let logging crash the app
  }
}

/**
 * Logger instance for application-wide logging
 */
/* eslint-disable no-console */
const logger = {
  /**
   * Log debug information (development only)
   * @param {...any} args - Arguments to log
   */
  debug: (...args) => {
    if (isDevelopment) {
      console.debug('[DEBUG]', ...args);
    }
  },

  /**
   * Log informational messages (development only)
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Log warning messages (persisted to Firestore)
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    console.warn('[WARN]', ...args);
    _persistLog('warn', args);
  },

  /**
   * Log error messages (persisted to Firestore)
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    console.error('[ERROR]', ...args);
    _persistLog('error', args);
  },

  /**
   * Log table data (development only)
   * @param {any} data - Data to display as table
   */
  table: (data) => {
    if (isDevelopment) {
      console.table(data);
    }
  },

  /**
   * Start a timer (development only)
   * @param {string} label - Timer label
   */
  time: (label) => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  /**
   * End a timer (development only)
   * @param {string} label - Timer label
   */
  timeEnd: (label) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  },
};

/* eslint-enable no-console */
export default logger;
